import throttle from 'lodash/throttle';
import Resizable from 'resizable';

import {
  HORSE_INFO_DATA_UPDATED,
  POPUP_INTERACTION,
  VIEW_HORSEINFO_RACE,
} from 'atg/constants/Events';
import { HORSE_INFO_POPUP } from 'atg/constants/Popups';
import observeStore from 'atg/redux/observeStore';
import EventDispatcher from 'atg/utils/EventDispatcher';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { MANAGE_HORSE } from 'shared/constants/DialogTypes';
import { fetchStartComments } from 'shared/redux/actions/monitorActions';
import { dialogs } from 'shared/redux/selectors';
import {
  getHorses,
  startCommentsSelector,
} from 'shared/redux/selectors/monitorSelectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import HorseTemplate from './HorseTemplate';

const $ = window.$;

let active = null;
let $mainScrollContainer = null;

export const create = (api, $scrollContainer, props) => {
  $mainScrollContainer = $scrollContainer;
  $mainScrollContainer.addClass('no-touch-scroll');

  if (active) {
    active.setProps(props);
    active.$scrollContainer.scrollTop(0);
    active.render();
  } else {
    active = new HorseInfoPopup(api, props);
  }
};

export const close = () => {
  if (active) {
    active.close();
  }
};

const fetchAllStartCommentsFromHorseInfoSet = horseInfoSet => {
  const startIdSet = new Set();
  const horseInfos = horseInfoSet.getHorseInfos();
  for (const horseInfo of horseInfos) {
    const raceCommentCount = horseInfo.getNumberOfRaceComments();
    for (let i = 0; i < raceCommentCount; i++) {
      const startId = horseInfo.getRaceComment(i).getStartId();
      if (startId) {
        startIdSet.add(startId);
      }
    }
  }
  return getStore().dispatch(fetchStartComments([...startIdSet]));
};

export const updateData = async props => {
  if (!active) {
    return;
  }

  if (INCLUDE_HORSE_MONITORING) {
    await fetchAllStartCommentsFromHorseInfoSet(props.dataSet);
  }

  active.setProps(props);
  active.render();
};

class HorseInfoPopup {
  constructor(api, props) {
    this.api = api;
    this.setProps(props);

    this.close = this.close.bind(this);
    this.render = this.render.bind(this);

    this.$outerContainer = StickyTemplate.jq('horse-info');
    $('#app').append(this.$outerContainer);

    this.$container = this.$outerContainer.find('.horse-info-container');
    this.$scrollContainer = this.$container.find('.scroll-container');
    this.$contentContainer = this.$container.find('.content-container');
    this.$navigation = this.$container.find('.horse-navigation');
    this.$loader = this.$container.find('.horse-info-loader');
    this.topHeight = this.$container.find('.horse-info-top').height();

    this.$loader.css({ top: this.topHeight });
    this.$container.find('button.close').event('click', this.close);
    this.setScrollContainerSize();

    EventDispatcher.addListener(HORSE_INFO_DATA_UPDATED, this.render);
    EventDispatcher.addListener(POPUP_INTERACTION, this.topPopup.bind(this));
    this.destroyStoreDialogsObserver = observeStore(
      getStore(),
      this.onDialogsChange,
      dialogs.getDialogs,
    );

    this.destroyStoreStartCommentsObserver = observeStore(
      getStore(),
      this.onMonitorDataChange,
      startCommentsSelector,
    );

    this.destroyStoreMyHorsesObserver = observeStore(
      getStore(),
      this.onMonitorDataChange,
      getHorses,
    );

    this.resizable = new Resizable(this.$container.get(0), {
      draggable: {
        handle: this.$container.find('.horse-info-top-bar').get(0),
      },
      handles: 's, se, e, w, sw',
      resize: () => {
        this.isUserResized = true;
        this.setScrollContainerSize();
      },
    });

    this.throttledScroll = throttle(this.onScroll.bind(this), 100);
    this.$scrollContainer.on('scroll', this.throttledScroll);
    this.throttledResize = throttle(this.onWindowResize.bind(this), 100);
    $(window).on('resize', this.throttledResize);
    this.$container.event('down', this.onPopupInteraction.bind(this));

    EventDispatcher.emit(POPUP_INTERACTION, HORSE_INFO_POPUP);

    this.render();
  }

  setProps({
    dataSet,
    numberOfStarts,
    raceIndex,
    startNumber = 1,
    raceDetails,
    isSingleView = false,
  }) {
    if (raceIndex !== this.raceIndex || isSingleView) {
      this.isUserResized = false;
    }
    this.dataSet = dataSet;
    this.numberOfStarts = numberOfStarts;
    this.raceIndex = raceIndex;
    this.selectedStartNumber = startNumber;
    this.raceDetails = raceDetails;
    this.isSingleView = undefined;
  }

  onDialogsChange = openDialogs => {
    // If there is a dialog stack where the bottom one isn't a ManageHorseDialog
    // then close the Horse Info. ManageHorseDialogs are excempt because HorseInfo
    // can open a ManageHorseDialog.
    if (openDialogs.length > 0 && openDialogs[0].type !== MANAGE_HORSE) {
      this.close();
    }
  };

  onMonitorDataChange = () => {
    // If any changes to the relevant parts of the store occur while a
    // ManageHorseDialog is showing (e.g. user has edited a start comment)
    // then re-render this HorseInfo.
    const openDialogs = dialogs.getDialogs(getStore().getState());
    if (openDialogs.length > 0 && openDialogs[0].type === MANAGE_HORSE) {
      this.render(true);
    }
  };

  onWindowResize() {
    this.resize();
  }

  resize($selectedHorsePage) {
    const css = {};

    if (!this.shouldShowLoader()) {
      css.maxHeight = Math.min(
        this.topHeight + this.$contentContainer.outerHeight(),
        window.innerHeight,
      );
    }

    let newHeight;

    if ($selectedHorsePage) {
      newHeight = this.topHeight + $selectedHorsePage.outerHeight();

      if (!this.isUserResized || newHeight > this.$container.height()) {
        css.height = newHeight;
      }
    }

    if (!this.isUserResized) {
      const ww = window.innerWidth;
      const wh = window.innerHeight;
      const idealW =
        Number.parseInt(this.$contentContainer.css('min-width'), 10) + 25;
      let cw = this.$container.width();
      const ch = newHeight || this.$container.height();

      // TODO clean up
      const grid = $('.grid');
      const defaultTop = Math.max(
        grid.offset().top + $('.bet-body').scrollTop() - 10,
        0,
      );

      css.top = defaultTop;

      if (cw > ww) {
        css.width = cw = ww;
      } else if (cw < idealW) {
        css.width = cw = Math.min(ww, idealW);
      }

      if (ch + defaultTop > wh) {
        css.height = wh;
        css.top = 0;
      }

      const isResizable = cw < ww || ch < wh; // Allow drag and resize unless we take up the whole screen
      if (!isResizable && this.resizable) {
        this.resizable.destroy();
        this.resizable = null;
        this.$outerContainer.css({
          overflow: 'hidden',
        });
      }
    }

    this.$container.css(css);
    this.setScrollContainerSize();
  }

  shouldShowLoader() {
    return !this.dataSet;
  }

  renderLoader() {
    if (this.loaderActive) {
      return;
    }

    this.loaderActive = true;
    const heading = Text.get('main-loader-horse-info-heading');
    this.$loader.find('h1').html(heading || '&nbsp;');
    this.$loader.fadeIn(200);
  }

  hideLoader() {
    if (!this.loaderActive) {
      return;
    }

    this.loaderActive = false;
    this.$loader.fadeOut(200);
  }

  renderHorse(startNumber, commentsPublishDate, isLoggedIn) {
    const horseInfo = this.dataSet.getHorseInfo(startNumber);
    if (!horseInfo) {
      return;
    }

    const callbackOptions = {
      row: this.raceIndex,
      startNumber: this.selectedStartNumber,
      sku: this.dataSet.getSKU(),
    };

    const tpl = new HorseTemplate(
      startNumber,
      horseInfo,
      this.raceIndex,
      this.api,
      commentsPublishDate,
      isLoggedIn,
      callbackOptions,
    );
    const $page = tpl.render();

    this.$contentContainer.append($page);
  }

  renderRaceDetails() {
    const tpl = StickyTemplate.jq('race-details', {
      '%id%': 0,
      '%race-details-header%': Text.get('race-details-header'),
      '%race-details-body%': this.raceDetails,
    });

    this.$contentContainer.append(tpl);
  }

  renderNavigation() {
    this.$navigation.empty();
    let i = this.raceDetails ? 0 : 1;
    for (; i <= this.numberOfStarts; i++) {
      const tpl = StickyTemplate.jq('horse-info-navigation-item', {
        '%id%': i,
        '%startnumber%': i === 0 ? 'Prop' : i,
      });
      if (i === 0) {
        tpl.addClass('race-detail-navigation');
      }
      if (i === this.selectedStartNumber) {
        tpl.addClass('active');
      }
      tpl.on('click', this.onNavigationClick.bind(this, i));
      tpl.appendTo(this.$navigation);
    }
  }

  render(noScrollingNoResize) {
    this.renderNavigation();

    if (this.shouldShowLoader()) {
      this.renderLoader();
      this.resize();
    } else {
      this.hideLoader();
      this.$contentContainer.empty();
      const commentsPublishDate = this.dataSet.getCommentsPublishDate();
      const isLoggedIn = this.dataSet.getIsLoggedIn();

      if (this.raceDetails) {
        this.renderRaceDetails();
      }

      if (this.isSingleView) {
        this.renderHorse(
          this.selectedStartNumber,
          commentsPublishDate,
          isLoggedIn,
        );
      } else {
        for (let i = 0; i < this.numberOfStarts; i++) {
          this.renderHorse(i + 1, commentsPublishDate, isLoggedIn);
        }
      }

      if (this.$contentContainer.find('.horse-info-page').length === 0) {
        this.close();
        return;
      }

      this.$container.addClass('with-data');

      if (noScrollingNoResize) {
        return;
      }

      const $shownPage = this.findHorsePage(
        this.selectedStartNumber ? this.selectedStartNumber : 1,
      );

      this.resize($shownPage);
      this.scrollToHorse($shownPage);
    }
  }

  close(immediate) {
    active = null;

    const f = () => {
      this.$outerContainer.remove();
    };

    EventDispatcher.removeListener(HORSE_INFO_DATA_UPDATED, this.render);
    EventDispatcher.removeListener(POPUP_INTERACTION, this.topPopup);
    this.destroyStoreDialogsObserver();
    this.destroyStoreStartCommentsObserver();
    this.destroyStoreMyHorsesObserver();
    this.throttledScroll.cancel();
    this.$scrollContainer.unevent('scroll', this.throttledScroll);
    this.throttledResize.cancel();
    $(window).unevent('resize', this.throttledResize);
    if (this.resizable) {
      this.resizable.destroy();
    }
    $mainScrollContainer.removeClass('no-touch-scroll');

    if (immediate === true) {
      f();
    } else {
      this.$container.fadeOut(HorseInfoPopup.fadeDuration / 2, f);
    }
  }

  setScrollContainerSize() {
    this.$scrollContainer.height(this.$container.height() - this.topHeight);
    this.$scrollContainer.width(this.$container.width());
  }

  findHorsePage(startNumber) {
    const $target =
      startNumber !== 0
        ? this.$contentContainer.find(
            `.horse-info-page[data-id=${startNumber}]`,
          )
        : this.$contentContainer.find(`.race-details[data-id=${startNumber}]`);
    return $target.length === 0 ? null : $target;
  }

  scrollToHorse(target) {
    if (!target) {
      return;
    }
    this.$scrollContainer.scrollTop(target.position().top);
  }

  setSelectedHorse(startNumber) {
    this.$navigation
      .find(`li[data-id="${this.selectedStartNumber}"]`)
      .removeClass('active');
    this.selectedStartNumber = startNumber;
    this.$navigation
      .find(`li[data-id="${this.selectedStartNumber}"]`)
      .addClass('active');
  }

  onNavigationClick(startNumber) {
    this.setSelectedHorse(startNumber);
    const $scrollTarget = this.findHorsePage(startNumber);

    if ($scrollTarget) {
      if (!$scrollTarget.hasClass('race-details')) {
        this.resize($scrollTarget);
      }
      this.scrollToHorse($scrollTarget);
    } else {
      EventDispatcher.emit(VIEW_HORSEINFO_RACE, this.raceIndex, startNumber);
    }
  }

  // Change selected horse to the one scrolled to
  onScroll() {
    const scrollTop = this.$scrollContainer.scrollTop();
    let $el, top;
    this.$contentContainer
      .find('.horse-info-page, .race-details')
      .each((i, el) => {
        $el = $(el);
        top = $el.position().top;
        if (scrollTop >= top && scrollTop <= top + $el.outerHeight()) {
          this.setSelectedHorse($el.data('id'));
          return;
        }
      });
  }

  topPopup(popup) {
    if (popup === HORSE_INFO_POPUP) {
      this.$outerContainer.addClass('top-popup');
    } else {
      this.$outerContainer.removeClass('top-popup');
    }
  }

  onPopupInteraction() {
    if (!this.$outerContainer.hasClass('top-popup')) {
      EventDispatcher.emit(POPUP_INTERACTION, HORSE_INFO_POPUP);
    }
  }
}
